import React, { useState } from "react";
import { MdDeleteOutline, MdEdit } from "react-icons/md";
import {
  addNewUser,
  deleteUser,
  updateUser,
} from "../../services/api/AdminService";
import toast from "react-hot-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { MdSearch } from "react-icons/md";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

function UsersTable({ users, onUsersChange }) {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [fullname, setFullname] = useState("");
  const [email, setEmail] = useState("");
  const [isBuilder, setIsBuilder] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  const resetForm = () => {
    setFullname("");
    setEmail("");
    setIsBuilder(false);
    setIsAdmin(false);
  };

  const handleAddNewUser = async () => {
    setIsSubmitting(true);
    const res = await addNewUser(fullname, email, isBuilder, isAdmin);
    setIsSubmitting(false);
    if (res.status === 200) {
      setShowAddDialog(false);
      onUsersChange([...users, res.data]);
      toast.success("User created.");
      resetForm();
    } else if (res.status === 409) {
      toast.error("User already exists.");
    } else {
      toast.error("Something went wrong.");
    }
  };

  const handleDeleteUser = async (sub) => {
    await deleteUser(sub);
    onUsersChange(users.filter((u) => u.sub !== sub));
    toast.success("User removed.");
  };

  const handleOpenEdit = (user) => {
    setEditingUser(user);
    setIsBuilder(user.is_builder);
    setIsAdmin(user.is_admin);
    setShowEditDialog(true);
  };

  const handleUpdateUser = async () => {
    setIsSubmitting(true);
    const res = await updateUser(editingUser.sub, isBuilder, isAdmin);
    setIsSubmitting(false);
    if (res && res.status === 200) {
      onUsersChange(
        users.map((u) =>
          u.sub === editingUser.sub
            ? { ...u, is_builder: isBuilder, is_admin: isAdmin }
            : u,
        ),
      );
      setShowEditDialog(false);
      toast.success("User updated.");
    } else {
      toast.error("Something went wrong.");
    }
  };

  const filtered = users.filter((u) =>
    u.fullname.toLowerCase().includes(search.toLowerCase())
  );
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginated = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-gray-500">
          {users.length} member{users.length !== 1 ? "s" : ""} in workspace
        </p>
        <Button
          onClick={() => {
            resetForm();
            setShowAddDialog(true);
          }}
        >
          Add user
        </Button>
      </div>

      <div className="relative mb-4">
        <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-lg pointer-events-none" />
        <Input
          placeholder="Search by name..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="pl-9"
        />
      </div>

      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-primary text-white">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Name</th>
              <th className="text-left px-4 py-3 font-medium">Email</th>
              <th className="text-left px-4 py-3 font-medium">Roles</th>
              <th className="px-4 py-3 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  className="text-center py-16 text-muted-foreground"
                >
                  {search ? "No users match your search." : "No users found."}
                </td>
              </tr>
            ) : (
              paginated.map((user) => (
                <tr
                  key={user.sub}
                  className="border-t hover:bg-muted/30 transition-colors"
                >
                  <td className="px-4 py-3 font-medium">{user.fullname}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {user.email}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1.5 flex-wrap">
                      {user.is_admin && <Badge>Admin</Badge>}
                      {user.is_builder && (
                        <Badge className={"bg-secondary"}>Builder</Badge>
                      )}
                      {!user.is_admin && !user.is_builder && (
                        <Badge variant="outline">Viewer</Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenEdit(user)}
                        title="Edit roles"
                      >
                        <MdEdit className="text-lg" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            title="Remove user"
                          >
                            <MdDeleteOutline className="text-lg" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Remove user</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to remove{" "}
                              <strong>{user.fullname}</strong> from the
                              workspace? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              onClick={() => handleDeleteUser(user.sub)}
                            >
                              Remove
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm text-muted-foreground">
          <span>
            Page {currentPage} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Add User Dialog */}
      <Dialog
        open={showAddDialog}
        onOpenChange={(open) => {
          setShowAddDialog(open);
          if (!open) resetForm();
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add new user</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="fullname">Full name</Label>
              <Input
                id="fullname"
                placeholder="Jane Doe"
                value={fullname}
                onChange={(e) => setFullname(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="jane@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-3 pt-1">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="builder-switch">Builder</Label>
                  <p className="text-xs text-muted-foreground">
                    Can create and manage chatbots
                  </p>
                </div>
                <Switch
                  id="builder-switch"
                  checked={isBuilder}
                  onCheckedChange={setIsBuilder}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="admin-switch">Admin</Label>
                  <p className="text-xs text-muted-foreground">
                    Full workspace access
                  </p>
                </div>
                <Switch
                  id="admin-switch"
                  checked={isAdmin}
                  onCheckedChange={setIsAdmin}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddNewUser}
              disabled={!fullname || !email || isSubmitting}
            >
              {isSubmitting ? "Adding..." : "Add user"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Roles Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit roles</DialogTitle>
          </DialogHeader>
          {editingUser && (
            <div className="space-y-4 py-2">
              <div className="rounded-md bg-muted px-3 py-2.5">
                <p className="text-sm font-medium">{editingUser.fullname}</p>
                <p className="text-xs text-muted-foreground">
                  {editingUser.email}
                </p>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="edit-builder-switch">Builder</Label>
                    <p className="text-xs text-muted-foreground">
                      Can create and manage chatbots
                    </p>
                  </div>
                  <Switch
                    id="edit-builder-switch"
                    checked={isBuilder}
                    onCheckedChange={setIsBuilder}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="edit-admin-switch">Admin</Label>
                    <p className="text-xs text-muted-foreground">
                      Full workspace access
                    </p>
                  </div>
                  <Switch
                    id="edit-admin-switch"
                    checked={isAdmin}
                    onCheckedChange={setIsAdmin}
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateUser} disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default UsersTable;
